import { isJapaneseHoliday } from "@chillnn-core/holiday-jp";
import { memo, useCallback, useMemo, useState } from "react";

import { cn } from "@/lib/chillnn/cn";
import dayjs from "@/lib/dayjs";
import { isPastDate } from "@/lib/dayjs/is-past-date";

import type { ButtonProps } from "@/components/ui/button/button";
import { Button } from "@/components/ui/button/button";
import { InputNumberField } from "@/components/ui/form/fields/input-field/input-number";

type Props = {
	stockDailyItem: {
		date: string;
		isAvailable: boolean;
		isEditable?: boolean;
		remainNum: number;
		soldNum: number;
		isSetPrice?: boolean;
	};
	onSetStock: (isAvailable: boolean, remainNum: number) => void;
	itemName?: string;
};

export const getStockDailyItemID = (date: string) => `StockDailyItem_Mobile_${date}`;

export const StockDailyItemMobile = memo(
	({
		stockDailyItem: {
			date, //
			remainNum,
			soldNum,
			isAvailable,
			isEditable,
			isSetPrice,
		},
		onSetStock,
		itemName,
	}: Props) => {
		const [isAvailableLocal, setIsAvailableLocal] = useState(isAvailable);
		const [remainNumLocal, setRemainNumLocal] = useState(remainNum);

		const handleClickIsAvailable = useCallback(() => {
			setIsAvailableLocal(!isAvailableLocal);
			onSetStock(!isAvailableLocal, remainNumLocal);
		}, [onSetStock, isAvailableLocal, remainNumLocal]);

		const handleChangeRemainNum = useCallback(
			(remainNum: number | undefined) => {
				setRemainNumLocal(remainNum ?? 0);
				onSetStock(isAvailableLocal, remainNum ?? 0);
			},
			[onSetStock, isAvailableLocal],
		);

		// 料金が設定されていない場合はアラートを表示（現状部屋でしか日ごとに料金設定できないので、部屋在庫管理画面以外で表示されることはない）
		const shouldShowAlert = isSetPrice === false;

		const inputDisabled = useMemo(() => {
			if (isPastDate(date)) {
				return true;
			}

			if (typeof isEditable === "boolean") {
				return !isEditable;
			}

			return false;
		}, [isEditable, date]);

		const buttonText = useMemo<"在庫無" | "売止" | "販売中" | "不可">(() => {
			if (isPastDate(date)) {
				return "不可";
			}
			if (typeof isEditable === "boolean" && !isEditable) {
				return "売止";
			}
			if (typeof isEditable === "boolean" && isEditable && remainNumLocal === 0) {
				return "在庫無";
			}

			return isAvailableLocal ? "販売中" : "売止";
		}, [date, isAvailableLocal, isEditable, remainNumLocal]);

		const buttonVariant = useMemo<ButtonProps["variant"]>(() => {
			if (isPastDate(date)) {
				return "disabled";
			}
			if (typeof isEditable === "boolean" && !isEditable) {
				return "disabled";
			}
			if (typeof isEditable === "boolean" && isEditable && remainNumLocal === 0) {
				return "disabled";
			}

			return isAvailableLocal ? "status-in-sale" : "status-not-for-sale";
		}, [date, isEditable, remainNumLocal, isAvailableLocal]);

		const dateText = dayjs(date).format("YYYY年MM月DD日（");
		const weekdayText = dayjs(date).format("dd");
		const weekdayColorClass = useMemo(() => {
			const d = new Date(date);
			const holiday = isJapaneseHoliday(d);
			const dayOfWeek = d.getDay();
			if (dayOfWeek === 0 || holiday) return "text-not-entered";
			if (dayOfWeek === 6) return "text-edited";
			return undefined;
		}, [date]);

		return (
			<div
				id={getStockDailyItemID(date)}
				className={cn("relative rounded border border-zinc-200 p-4", isPastDate(date) && "pointer-events-none")}
			>
				{isPastDate(date) && (
					<div className="absolute left-0 top-0 z-20 h-full w-full bg-zinc-200 opacity-70" />
				)}

				<div className="relative space-y-4">
					<div>
						<p className="text-lg font-semibold">
							{dateText}<span className={weekdayColorClass}>{weekdayText}</span>）
						</p>
					</div>
					<div>
						<p className="line-clamp-2 text-sm">{itemName}</p>
					</div>

					{!isPastDate(date) &&
						(shouldShowAlert ? (
							<div className="absolute left-0 top-3 z-10 h-[calc(100%-0.75rem)] w-full rounded bg-white py-4 text-left">
								<div>
									<p className="line-clamp-2 text-sm">{itemName}</p>
								</div>

								<div className="space-y-1 text-sm">
									<p>
										CHILLNNで料金を管理している場合は"料金ランク"から、サイトコントローラーで料金を管理している場合は、サイトコントローラーの管理画面から料金を設定してください。
									</p>
									<p className="text-xs text-brand">
										※紐付けているプランで "定額部屋料金"
										を設定している場合は、そのプランでのみ販売されます。
									</p>
								</div>
							</div>
						) : !isEditable ? (
							<div className="absolute left-0 top-3 z-10 h-[calc(100%-0.75rem)] w-full space-y-4 rounded bg-white py-4 text-left">
								<div>
									<p className="line-clamp-2 text-sm">{itemName}</p>
								</div>
								<div className="space-y-1 text-sm">
									<div>
										<p>商品編集ページの設定によって、販売されていません。</p>
										<p className="text-sm">
											※予約の開始可能日時、予約の締切日時、翌朝のオプションなど
										</p>
									</div>
									<p>
										販売開始するには、「商品管理/各商品編集ページ」で該当する設定を変更してください。
									</p>
								</div>
							</div>
						) : null)}

					<InputNumberField
						label="残在庫"
						value={remainNumLocal}
						disabled={inputDisabled}
						handleInput={handleChangeRemainNum}
					/>

					<div>
						<p className="text-sm">販売済み</p>
						<p className="text-lg font-semibold">{String(soldNum)}</p>
					</div>

					<div className="flex flex-col">
						<div className="flex gap-x-1 text-xs">
							<Button
								onClick={handleClickIsAvailable}
								variant={buttonVariant}
								size="sm"
								className="w-full"
							>
								<p>{buttonText}</p>
							</Button>
						</div>
					</div>
				</div>
			</div>
		);
	},
);

StockDailyItemMobile.displayName = "StockDailyItemMobile";
